import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { format } from 'date-fns';

const METRICS = (t) => [
  { key: 'weight_kg',        label: t('clientCheckIns.statWeight'),         unit: 'kg' },
  { key: 'body_fat_percent', label: t('clientCheckIns.statBodyFat'),        unit: '%' },
  { key: 'waist_cm',         label: t('clientDetail.measurementWaist'),     unit: 'cm' },
  { key: 'chest_cm',         label: t('clientDetail.measurementChest'),     unit: 'cm' },
  { key: 'hips_cm',          label: t('clientDetail.measurementHips'),      unit: 'cm' },
  { key: 'adherence_percent',label: t('clientCheckIns.statAdherence'),      unit: '%' },
];

export default function CheckInTrendChart({ checkIns, selectedId, onSelect }) {
  const { t } = useTranslation();
  const metrics = METRICS(t);
  const [metric, setMetric] = useState('weight_kg');

  const series = useMemo(() => {
    return [...checkIns]
      .reverse() // oldest first
      .filter(ci => ci[metric] != null)
      .map(ci => ({
        id: ci.id,
        date: ci.date,
        label: ci.date ? format(new Date(ci.date), 'dd MMM') : '',
        value: ci[metric],
      }));
  }, [checkIns, metric]);

  const selectedMeta = metrics.find(m => m.key === metric);

  if (series.length === 0) return null;

  return (
    <div className="rounded-xl bg-muted/20 border border-border/40 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-muted-foreground uppercase">
          {t('clientCheckIns.sectionTrend', 'Trend')}
        </span>
        <Select value={metric} onValueChange={setMetric}>
          <SelectTrigger className="h-7 text-xs w-auto gap-1.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {metrics.map(m => (
              <SelectItem key={m.key} value={m.key} className="text-xs">
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="h-[120px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={['dataMin - 1', 'dataMax + 1']}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={false}
              tickLine={false}
              width={32}
            />
            <Tooltip
              contentStyle={{ background: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 11 }}
              formatter={(value) => [`${value}${selectedMeta?.unit || ''}`, selectedMeta?.label || metric]}
              labelFormatter={(label) => label}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload } = props;
                const isSel = payload.id === selectedId;
                return (
                  <circle
                    key={payload.id}
                    cx={cx}
                    cy={cy}
                    r={isSel ? 5 : 3}
                    fill={isSel ? 'hsl(var(--primary))' : 'hsl(var(--background))'}
                    stroke="hsl(var(--primary))"
                    strokeWidth={2}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelect?.(payload.id)}
                  />
                );
              }}
              activeDot={{ r: 6, fill: 'hsl(var(--primary))' }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
